import { getCancelOrderContent } from '@/api/order-api';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { CancelOrder, OrderStatus } from '@/types/order-type';
import { Clock, DollarSign, Package2, User, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { CancelOrderList } from './CancelOrderList';
import { OrderStatusBadge } from './OrderStatusBadge';
import { removeOrderItem } from '@/api/sale-api';

export interface OrderSlotRowProps {
  orderId: string;
  slotNo: number;
  customerName?: string | null;
  totalProducts: number;
  totalAmount: number;
  createdAt: string;
  status: OrderStatus;
  orderNumber?: string;
  isAssigning?: boolean;
  isDraggingOver?: boolean;
  onClick?: () => void;
  refresh?: () => void;
  variantId: string;
  productVariantName?: string;
}

export function OrderSlotRow({
  slotNo,
  customerName,
  totalProducts,
  totalAmount,
  createdAt,
  status,
  isAssigning = false,
  isDraggingOver = false,
  orderId,
  orderNumber,
  variantId,
  onClick,
  refresh,
  productVariantName,
}: OrderSlotRowProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelOrders, setCancelOrders] = useState<CancelOrder[]>([]);

  const handleOpenConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirm(true);
  };

  const handleConfirmCancel = async () => {
    setIsCancelling(true);
    try {
      const resp = await removeOrderItem(variantId, orderId);
      if (resp.success) {
        const removeResult = resp.data;
        if (removeResult.cancelOrder) {
          toast({ title: 'Success', description: `Order cancelled successfully since customer only order this item : ${removeResult.removeQty}` });
        }
        else {
          toast({ title: 'Success', description: `Item has been remove successfully : [${removeResult.removeQty}] pieces` });
        }
        if (refresh) refresh();
      } else {
        const msg = resp.message || 'Failed to cancel order';
        toast({ title: 'Error', description: msg, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to cancel order', variant: 'destructive' });
    } finally {
      setIsCancelling(false);
      setShowConfirm(false);
    }
  };
  const fetchCancelOrders = async () => {
    const response = await getCancelOrderContent(orderId);
    if (response.success) {
      setCancelOrders(response.data);
    } else {
      toast({
        title: 'Error',
        description: 'Failed to fetch cancel orders',
        variant: 'destructive',
      });
    }
  };
  useEffect(() => {
    if (!orderId || OrderStatus.CANCELLED != status) return;
    console.log('fetching cancel orders for order id: ', orderId);
    fetchCancelOrders();
  }, [orderId]);

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      className={cn(
        'box-border grid h-[88px] min-h-[88px] max-h-[88px] shrink-0 cursor-pointer grid-cols-[44px,1fr] overflow-hidden border-b border-slate-100 outline-none transition-all group sm:grid-cols-[52px,1fr] md:grid-cols-[60px,1fr]',
        isAssigning ? 'opacity-80' : 'hover:bg-green-50/40',
        isDraggingOver && 'border-green-400 bg-green-100/60',
        'focus-visible:bg-slate-50',
      )}
    >
      <div className="flex h-[88px] items-center justify-center border-r border-slate-100 px-1 sm:px-2 md:px-3">
        <span
          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-[11px] font-semibold text-green-700 shadow-sm transition group-hover:bg-green-200 sm:h-7 sm:w-7 sm:text-[12px]"
        >
          {slotNo}
        </span>
      </div>

      <div className="flex h-[88px] min-w-0 flex-col justify-center gap-0.5 overflow-hidden px-2 sm:px-3">
        <div className="flex min-w-0 items-center justify-between gap-1">
          <span className="flex min-w-0 items-center gap-1 text-xs font-semibold text-slate-800 sm:text-sm">
            <User className="h-3.5 w-3.5 shrink-0 text-slate-400 sm:h-4 sm:w-4" />
            <span className="truncate">{customerName || 'Walk-in Customer'}</span>
            {orderNumber ? (
              <span className="hidden shrink-0 text-[10px] text-slate-400 sm:inline">#{orderNumber}</span>
            ) : null}
          </span>

          {status === OrderStatus.CONFIRMED && (
            <Button
              variant="outline"
              size="icon"
              onClick={handleOpenConfirm}
              className="h-6 w-6 shrink-0 rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 sm:h-7 sm:w-7"
              title="Remove item from order"
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          )}
          {status === OrderStatus.CANCELLED && <CancelOrderList logs={cancelOrders} />}
        </div>

        <div className="flex min-w-0 items-center gap-2 text-[10px] text-slate-600 sm:gap-3 sm:text-xs">
          <span className="flex shrink-0 items-center gap-1">
            <Package2 className="h-3 w-3 text-slate-400 sm:h-3.5 sm:w-3.5" />
            {totalProducts} item{totalProducts !== 1 ? 's' : ''}
          </span>

          <span className="flex shrink-0 items-center gap-1">
            <DollarSign className="h-3 w-3 text-slate-400 sm:h-3.5 sm:w-3.5" />
            {totalAmount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          <OrderStatusBadge status={status} />
        </div>

        <div className="hidden items-center gap-1 truncate text-[10px] text-slate-400 sm:flex sm:text-[11px]">
          <Clock className="h-3 w-3 text-slate-400 sm:h-3.5 sm:w-3.5" />
          {new Date(createdAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              <AlertDialogTitle>Remove Item from Slot #{slotNo}?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to remove <strong>{productVariantName}</strong> from
                <strong> {customerName}</strong>'s selection?
              </p>
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200 text-xs">
                <strong>Note:</strong> This will restock the item immediately for other live viewers.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={(e) => e.stopPropagation()}
              className="h-9"
            >
              Keep Item
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600 h-9"
              disabled={isCancelling}
            >
              {isCancelling ? 'Removing...' : 'Confirm Removal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
